// =============================================================================
// SECURITY UTILITIES
// =============================================================================
// Purpose: Centralized security functions for input validation, sanitization,
//          rate limiting, and logging
// =============================================================================

// -------------------------------------------------------------------------
// TEXT SANITIZATION - Strips HTML tags and potential XSS vectors
// -------------------------------------------------------------------------

/**
 * Strips HTML tags and script content from user input
 * SECURITY: Prevents XSS attacks by removing dangerous HTML
 * @param {string} input - Raw user input
 * @returns {string} Sanitized string safe for display/storage
 */
export function sanitizeText(input) {
  if (typeof input !== "string") return "";

  return input
    // Remove script tags and content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    // Remove all HTML tags
    .replace(/<[^>]*>/g, "")
    // Remove javascript: and data: URLs
    .replace(/javascript:/gi, "")
    .replace(/data:/gi, "")
    // Remove event handlers (onclick, onerror, etc.)
    .replace(/on\w+\s*=/gi, "")
    // Trim whitespace
    .trim();
}

/**
 * Sanitizes an object's string fields
 * @param {Object} obj - Object with string fields
 * @param {Array<string>} fields - Fields to sanitize
 * @returns {Object} Object with sanitized fields
 */
export function sanitizeObject(obj, fields) {
  const sanitized = { ...obj };
  fields.forEach((field) => {
    if (typeof sanitized[field] === "string") {
      sanitized[field] = sanitizeText(sanitized[field]);
    }
  });
  return sanitized;
}

// -------------------------------------------------------------------------
// URL VALIDATION - Ensures only safe URLs are stored
// -------------------------------------------------------------------------

/**
 * Validates URL to ensure it's http/https only
 * SECURITY: Prevents javascript: and data: URL injection
 * @param {string} url - URL to validate
 * @returns {boolean} True if URL is valid and safe
 */
export function isValidUrl(url) {
  if (!url || typeof url !== "string") return false;

  try {
    const parsed = new URL(url.trim());
    // SECURITY: Only allow http and https protocols
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
}

/**
 * Validates URL and returns it or empty string
 * @param {string} url - URL to validate
 * @returns {string} Valid URL or empty string
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  return isValidUrl(trimmed) ? trimmed : "";
}

// -------------------------------------------------------------------------
// FIELD VALIDATION - Validates required fields and length limits
// -------------------------------------------------------------------------

/**
 * Validation rules for tool submissions
 */
export const VALIDATION_RULES = {
  toolName: {
    required: true,
    maxLength: 100,
    minLength: 2,
    label: "Tool Name",
  },
  toolUrl: {
    required: true,
    maxLength: 500,
    minLength: 10,
    label: "Tool URL",
    isUrl: true,
  },
  description: {
    required: true,
    maxLength: 500, // Limit description length
    minLength: 10,
    label: "Description",
  },
  category: {
    required: false,
    maxLength: 50,
    label: "Category",
  },
  useCase: {
    required: false,
    maxLength: 300,
    label: "Use Case",
  },
  discoveredVia: {
    required: false,
    maxLength: 200,
    label: "Discovered Via",
  },
  email: {
    required: false,
    maxLength: 254, // RFC 5321 limit
    label: "Email",
    isEmail: true,
  },
};

/**
 * Validates a single field against its rules
 * @param {string} fieldName - Field name
 * @param {string} value - Field value
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateField(fieldName, value) {
  const rules = VALIDATION_RULES[fieldName];
  if (!rules) return { valid: true };

  const trimmedValue = typeof value === "string" ? value.trim() : "";

  // Check required
  if (rules.required && !trimmedValue) {
    return { valid: false, error: `${rules.label} is required.` };
  }

  // Skip further checks if empty and not required
  if (!trimmedValue) return { valid: true };

  // Check min length
  if (rules.minLength && trimmedValue.length < rules.minLength) {
    return {
      valid: false,
      error: `${rules.label} must be at least ${rules.minLength} characters.`,
    };
  }

  // Check max length
  if (rules.maxLength && trimmedValue.length > rules.maxLength) {
    return {
      valid: false,
      error: `${rules.label} must be less than ${rules.maxLength} characters.`,
    };
  }

  // Check URL format
  if (rules.isUrl && !isValidUrl(trimmedValue)) {
    return {
      valid: false,
      error: `${rules.label} must be a valid URL (http:// or https://).`,
    };
  }

  // Check email format (basic)
  if (rules.isEmail && trimmedValue) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedValue)) {
      return { valid: false, error: `${rules.label} must be a valid email address.` };
    }
  }

  return { valid: true };
}

/**
 * Validates all fields in a form data object
 * @param {Object} formData - Form data object
 * @param {Array<string>} requiredFields - Fields to validate
 * @returns {{ valid: boolean, errors: Object }}
 */
export function validateForm(formData, requiredFields = []) {
  const errors = {};
  let valid = true;

  requiredFields.forEach((fieldName) => {
    const result = validateField(fieldName, formData[fieldName]);
    if (!result.valid) {
      errors[fieldName] = result.error;
      valid = false;
    }
  });

  return { valid, errors };
}

// -------------------------------------------------------------------------
// RATE LIMITING - Simple cooldown mechanism
// -------------------------------------------------------------------------

const RATE_LIMIT_KEY = "tolio_last_submission";
const RATE_LIMIT_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes cooldown

/**
 * Checks if user is rate limited (local storage based)
 * SECURITY: Prevents rapid-fire form submissions
 * @returns {{ allowed: boolean, remainingMs?: number }}
 */
export function checkRateLimit() {
  try {
    const lastSubmission = localStorage.getItem(RATE_LIMIT_KEY);
    if (!lastSubmission) {
      return { allowed: true };
    }

    const lastTime = parseInt(lastSubmission, 10);
    const now = Date.now();
    const elapsed = now - lastTime;

    if (elapsed < RATE_LIMIT_COOLDOWN_MS) {
      return {
        allowed: false,
        remainingMs: RATE_LIMIT_COOLDOWN_MS - elapsed,
      };
    }

    return { allowed: true };
  } catch {
    // If localStorage fails, allow submission
    return { allowed: true };
  }
}

/**
 * Records a submission timestamp for rate limiting
 */
export function recordSubmission() {
  try {
    localStorage.setItem(RATE_LIMIT_KEY, Date.now().toString());
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Formats remaining cooldown time for display
 * @param {number} ms - Milliseconds remaining
 * @returns {string} Human-readable time
 */
export function formatCooldownTime(ms) {
  const minutes = Math.ceil(ms / 60000);
  return minutes === 1 ? "1 minute" : `${minutes} minutes`;
}

// -------------------------------------------------------------------------
// SECURITY LOGGING - Logs security events (dev only, not exposed to users)
// -------------------------------------------------------------------------

/**
 * Logs security-related events to console (development only)
 * SECURITY: Never expose these logs to end users
 * @param {string} eventType - Type of security event
 * @param {Object} details - Event details (sanitized, no sensitive data)
 */
export function logSecurityEvent(eventType, details = {}) {
  // Only log in development
  if (process.env.NODE_ENV !== "production") {
    console.warn(`[SECURITY] ${eventType}:`, {
      timestamp: new Date().toISOString(),
      ...details,
    });
  }

  // In production, you could send to a logging service
  // Example: sendToLoggingService(eventType, details);
}

/**
 * Logs failed write attempts
 * @param {string} collection - Firestore collection
 * @param {string} reason - Reason for failure
 * @param {Object} data - Sanitized data (no PII)
 */
export function logFailedWrite(collection, reason, data = {}) {
  logSecurityEvent("FAILED_WRITE", {
    collection,
    reason,
    // Only log non-sensitive data
    dataKeys: Object.keys(data),
  });
}

/**
 * Logs rate limit violations
 * @param {string} action - Action that was rate limited
 */
export function logRateLimitViolation(action) {
  logSecurityEvent("RATE_LIMIT_VIOLATION", { action });
}

/**
 * Logs validation failures
 * @param {Object} errors - Validation errors
 */
export function logValidationFailure(errors) {
  logSecurityEvent("VALIDATION_FAILURE", {
    failedFields: Object.keys(errors),
  });
}
